import dayjs from 'dayjs';

import { ValidatedPreset } from './types';
import { DatePeriod, FilterPreset, RangeCondition } from '../../types';
import { rangeToUtc } from '../../utils';

export const validatePreset = (
  preset: FilterPreset,
  context: {
    timezone: string;
    range: DatePeriod;
    startMonth?: RangeCondition;
    endMonth?: RangeCondition;
    maxNumberOfDays?: number;
  }
): ValidatedPreset => {
  const { timezone, range, startMonth, endMonth, maxNumberOfDays } = context;
  let error: string | undefined;
  const { from, to } = preset.date(timezone);
  // Convert UTC dates to target timezone while preserving the exact time component
  // This is important for accurate day difference calculations during DST transitions
  const rangeStartDate = dayjs.utc(from).tz(timezone);
  const rangeEndDate = dayjs.utc(to).tz(timezone);

  const startDate =
    typeof startMonth === 'function'
      ? startMonth({ range: rangeToUtc(range), timezone })
      : startMonth;

  const endDate =
    typeof endMonth === 'function'
      ? endMonth({ range: rangeToUtc(range), timezone })
      : endMonth;

  if (
    (startDate && rangeStartDate.isBefore(startDate)) ||
    (endDate && rangeEndDate.isAfter(endDate))
  ) {
    error = 'No data available for this period.';
  }

  if (maxNumberOfDays) {
    // Simulate a UTC date by replacing tz component with UTC and calculate as if there was no offset,
    // because dayjs has a bug in tz-aware date diff: https://github.com/iamkun/dayjs/issues/1224.
    // Using keepLocalTime = true ensures that we're getting a tz-aware diff.
    const tzFrom = rangeStartDate.utc(true);
    const tzTo = rangeEndDate.utc(true);

    const diffInDays = tzTo.diff(tzFrom, 'day', true);
    if (diffInDays > maxNumberOfDays) {
      error = 'Range is above max days limit.';
    }
  }

  return { ...preset, error };
};
